import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiChevronDown, mdiChevronRight, mdiPlus } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { EquipmentRow } from './EquipmentRow'
import { CONDITION_SEVERITY } from './condition-utils'
import {
  EQUIPMENT_TABLE_GRID,
  EQUIPMENT_TABLE_HEAD_CELL,
  EQUIPMENT_TABLE_MIN_WIDTH,
} from './equipment-table-columns'
import { cn } from '../../lib/utils'
import { AssetTypeTile } from '../assets/AssetTypeTile'
import type { EquipmentRow as EquipmentRowData, EquipmentTradeBucket } from '../../api/equipment-tab-types'
import { EmptyState } from '../ui/empty-state'
import { METERING_TRADE_CODE } from './trades'

interface TradeAccordionProps {
  org: string
  bucket: EquipmentTradeBucket
  /**
   * Fired when the user clicks the per-trade `+` button. The parent
   * (EquipmentTab) opens the AddEquipmentDialog with this trade
   * pre-selected.
   */
  onAddEquipment?: (tradeCode: string) => void
  /**
   * Whether equipment of this trade can be created under the current asset.
   * When false, the `+` affordance is hidden (location scoping). Defaults to
   * true so callers that don't scope keep the button.
   */
  creatable?: boolean
  /** Opens "Add a component" for an equipment row (spec 2026-10-27-17). */
  onAddComponent?: (assemblyUid: string) => void
}

const PAGE_SIZE = 10


/**
 * One accordion section per trade. Header anatomy per spec §6.2; row sort
 * per §6.4; truncation per §6.5.
 *
 * The per-trade `+` button raises `onAddEquipment(trade_code)` to the parent
 * EquipmentTab, which opens the centralised AddEquipmentDialog with the
 * trade preselected.
 */
export function TradeAccordion({
  org,
  bucket,
  onAddEquipment,
  creatable = true,
  onAddComponent,
}: TradeAccordionProps) {
  const { t } = useTranslation('assets')
  // Every trade starts collapsed, even one holding poor or end-of-life
  // equipment: auto-opening it pushed the top of the tab out of view on every
  // visit (#1698). The header's condition rollup already flags those trades.
  const [expanded, setExpanded] = useState(false)
  const [showAll, setShowAll] = useState(false)

  // Sort: condition desc → age/lifespan ratio desc → installed_at asc.
  const sortedEquipment = useMemo(() => {
    const list = [...(bucket.equipment || [])]
    list.sort((a, b) => {
      const sa = CONDITION_SEVERITY[a.condition] ?? 0
      const sb = CONDITION_SEVERITY[b.condition] ?? 0
      if (sa !== sb) return sb - sa
      const ra = a.lifespan_years ? (a.age_years || 0) / a.lifespan_years : 0
      const rb = b.lifespan_years ? (b.age_years || 0) / b.lifespan_years : 0
      if (ra !== rb) return rb - ra
      return (a.installed_at || '').localeCompare(b.installed_at || '')
    })
    return list
  }, [bucket.equipment])

  const visibleEquipment = showAll
    ? sortedEquipment
    : sortedEquipment.slice(0, PAGE_SIZE)
  const overflow = sortedEquipment.length - visibleEquipment.length

  const rollup = bucket.condition_rollup || {
    poor: 0,
    end_of_life: 0,
    unassessed: 0,
  }
  const allGood = bucket.count > 0 && !rollup.poor && !rollup.end_of_life

  return (
    <div
      className="border border-border rounded-md bg-card overflow-hidden"
      data-testid={`equipment-tab-zone-b-accordion-${bucket.trade_code}`}
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-3 py-2.5 bg-muted/40">
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="flex-shrink-0 text-muted-foreground hover:text-foreground"
          aria-label={
            expanded
              ? t('equipment_tab.zone_b.collapse')
              : t('equipment_tab.zone_b.expand')
          }
          data-testid={`equipment-tab-zone-b-accordion-${bucket.trade_code}-toggle`}
        >
          {expanded ? (
            <AppIcon path={mdiChevronDown} size="sm" tone="muted" />
          ) : (
            <AppIcon path={mdiChevronRight} size="sm" tone="muted" />
          )}
        </button>
        <div className="flex items-center gap-2 min-w-0 flex-1 flex-wrap">
          <span className="text-base font-semibold text-foreground flex items-center gap-1.5">
            <AssetTypeTile
              type={{ code: bucket.trade_code, kind: 'trade', icon: bucket.trade_icon }}
              size="sm"
            />
            <span>{bucket.trade_label}</span>
          </span>
          <span className="text-xs text-muted-foreground">
            {bucket.count === 0
              ? t('equipment_tab.zone_b.no_equipment')
              : t('equipment_tab.zone_b.equipment_count', { count: bucket.count })}
          </span>

          {/* Rollup chips */}
          {rollup.poor > 0 && (
            <span className="text-xs text-warning bg-warning/10 px-1.5 py-0.5 rounded">
              {t('equipment_tab.zone_b.rollup_poor', { count: rollup.poor })}
            </span>
          )}
          {rollup.end_of_life > 0 && (
            <span className="text-xs text-destructive bg-destructive/10 px-1.5 py-0.5 rounded">
              {t('equipment_tab.zone_b.rollup_end_of_life', { count: rollup.end_of_life })}
            </span>
          )}
          {rollup.unassessed > 0 && (
            <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
              {t('equipment_tab.zone_b.rollup_unassessed', { count: rollup.unassessed })}
            </span>
          )}
          {allGood && (
            <span className="text-xs text-success">
              · {t('equipment_tab.zone_b.all_good')}
            </span>
          )}
        </div>

        {/* The tab's « Ajouter » button covers a filled trade; the row's +
            stays where it saves a step: an empty trade, and metering, whose
            + opens the meters dialog (spec 2026-10-25-20). */}
        {onAddEquipment && creatable && (bucket.count === 0 || bucket.trade_code === METERING_TRADE_CODE) && (
          <button
            type="button"
            onClick={() => onAddEquipment(bucket.trade_code)}
            className="flex items-center gap-1 text-xs text-primary hover:underline px-2 py-1 rounded"
            aria-label={t('equipment_picker.add_to_trade_aria_label', { trade: bucket.trade_label })}
            title={t('equipment_picker.add_to_trade_aria_label', { trade: bucket.trade_label })}
            data-testid={`equipment-tab-zone-b-accordion-${bucket.trade_code}-add`}
          >
            <AppIcon path={mdiPlus} size="xs" />
          </button>
        )}
      </div>

      {/* Body */}
      {expanded && (
        <div>
          {sortedEquipment.length === 0 ? (
            <EmptyState variant="inline" title={t('equipment_tab.zone_b.empty_trade')} />
          ) : (
            <div className="overflow-x-auto">
              <div
                className={EQUIPMENT_TABLE_MIN_WIDTH}
                role="table"
                aria-label={bucket.trade_label}
              >
                <div
                  className={cn(EQUIPMENT_TABLE_GRID, 'px-4 py-2 border-t border-border/60 bg-muted/20')}
                  role="row"
                  data-testid={`equipment-tab-zone-b-accordion-${bucket.trade_code}-header`}
                >
                  {(
                    [
                      'equipment',
                      'condition',
                      'commissioned',
                      'maintenance',
                      'warranty',
                      'inspections',
                      'quantity',
                    ] as const
                  ).map((col) => (
                    <div
                      key={col}
                      role="columnheader"
                      title={t(`equipment_tab.columns.${col}`)}
                      className={cn(
                        EQUIPMENT_TABLE_HEAD_CELL,
                        col === 'quantity' && 'text-right',
                      )}
                    >
                      {t(`equipment_tab.columns.${col}`)}
                    </div>
                  ))}
                </div>
                {visibleEquipment.map((row) => (
                  <EquipmentRowTree key={row.id} org={org} row={row} depth={0} onAddComponent={onAddComponent} />
                ))}
              </div>
              {overflow > 0 && (
                <button
                  type="button"
                  onClick={() => setShowAll(true)}
                  className="block w-full text-left px-4 py-2.5 text-sm text-primary hover:underline border-t border-border/60"
                  data-testid={`equipment-tab-zone-b-accordion-${bucket.trade_code}-more`}
                >
                  {t('equipment_tab.zone_b.more_in_trade', { count: overflow })}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * An equipment row and, collapsed behind its « N composants » chip, the
 * components nested under it (spec 2026-10-27-17). A filter that matched a
 * component opens its equipment.
 */
export function EquipmentRowTree({
  org,
  row,
  depth,
  onAddComponent,
}: {
  org: string
  row: EquipmentRowData
  depth: number
  onAddComponent?: (assemblyUid: string) => void
}) {
  const [expanded, setExpanded] = useState(!!row.components_expanded)
  const [lastForced, setLastForced] = useState(!!row.components_expanded)
  // A new search that matches a component reopens its equipment.
  if (!!row.components_expanded !== lastForced) {
    setLastForced(!!row.components_expanded)
    if (row.components_expanded) setExpanded(true)
  }
  const components = row.components ?? []
  return (
    <>
      <EquipmentRow
        org={org}
        row={row}
        depth={depth}
        onAddComponent={onAddComponent}
        componentsToggle={
          components.length > 0
            ? { count: components.length, expanded, onToggle: () => setExpanded((e) => !e) }
            : undefined
        }
      />
      {expanded &&
        components.map((c) => (
          <EquipmentRowTree key={c.id} org={org} row={c} depth={depth + 1} onAddComponent={onAddComponent} />
        ))}
    </>
  )
}
